import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  Input,
  Select,
  Space,
  Tag,
  Typography,
  Divider,
  Row,
  Col,
  Form,
  Button,
  Upload,
  Empty,
  Modal,
  message,
  List,
  Spin,
  Popconfirm,
} from "antd";
import {
  Building2,
  SearchIcon,
  Trash2,
  Upload as UploadIcon,
} from "lucide-react";
import { ContactHistory } from "./contract-history";
import DrawerApp from "components/drawer-app";
import { ESTADOS, FAIXAS } from "views/util/filter";
import { notificationErroInternalGeneric } from "util/api-utils";
import {
  getCompanies,
  saveCompany,
  deleteCompany,
} from "services/managerial/managerial-service";
import { useDebounce } from "views/util/debounce";
import CompanyDetailBody from "./painel-company-detail";

const { Text, Title } = Typography;

export default function CompaniesPanel({
  protectedView,
  data,
  onDragStart,
  setEmpresas,
}) {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const empresas = useMemo(() => {
    return data;
  }, [data]);
  const [estadoFiltro, setEstadoFiltro] = useState();
  const [faixaFiltro, setFaixaFiltro] = useState();
  const [searchTerm, setSearchTerm] = useState("");

  const [sel, setSel] = useState(null);
  const [openDetailItem, setOpenDetailItem] = useState(false);
  const onCloseDetailItem = async () => {
    setOpenDetailItem(false);
    setSel(null);
    setLoading(true);

    try {
      const result = await getCompanies();
      setEmpresas(result);
    } catch {
      notificationErroInternalGeneric();
    } finally {
      setLoading(false);
    }
  };

  const [openCreate, setOpenCreate] = useState(false);

  const empresasFiltradas = useMemo(() => {
    return empresas.filter((e) => {
      const m1 = !estadoFiltro || e.state === estadoFiltro;
      const m2 = !faixaFiltro || e.billing === faixaFiltro;
      const m3 =
        !searchTerm ||
        e.name?.toLowerCase()?.includes(searchTerm) ||
        e.document?.toLowerCase()?.includes(searchTerm);
      return m1 && m2 && m3;
    });
  }, [empresas, estadoFiltro, faixaFiltro, searchTerm]);

  const [pdfModal, setPdfModal] = useState({
    open: false,
    url: null,
    name: "",
  });
  const openPdf = (file) => {
    const url = URL.createObjectURL(file.originFileObj || file);
    setPdfModal({ open: true, url, name: file.name || "Documento.pdf" });
  };
  const closePdf = () => {
    if (pdfModal.url) URL.revokeObjectURL(pdfModal.url);
    setPdfModal({ open: false, url: null, name: "" });
  };

  const uploadProps = (empresaKey) => ({
    multiple: true,
    beforeUpload: (file) => {
      if (file.type !== "application/pdf") {
        message.error("Apenas PDFs.");
        return Upload.LIST_IGNORE;
      }
      setEmpresas((prev) =>
        prev.map((e) =>
          e.key === empresaKey ? { ...e, pdfs: [...e.pdfs, file] } : e
        )
      );
      return false;
    },
    onPreview: (file) => openPdf(file),
    onRemove: (file) => {
      setEmpresas((prev) =>
        prev.map((e) =>
          e.key === empresaKey
            ? { ...e, pdfs: e.pdfs.filter((f) => f.uid !== file.uid) }
            : e
        )
      );
    },
  });
  const save = async (values) => {
    setLoading(true);
    const obj = {
      name: values.name,
      document: values.document,
      state: values.state,
      billing: values.billing,
    };
    try {
      await saveCompany(obj);
      const result = await getCompanies();
      setEmpresas(result);
      setOpenCreate(false);
      form.resetFields();
      message.success("Empresa criada");
    } catch (error) {
      notificationErroInternalGeneric();
    } finally {
      setLoading(false);
    }
  };

  const removeOne = async (id) => {
    await deleteCompany(id);
    onCloseDetailItem();
  };

  return (
    <Spin spinning={loading}>
      <Card
        title={
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Space>
              <Building2 size={18} style={{ marginTop: 4 }} />
              <Text>Empresas Liberadas ({empresasFiltradas.length})</Text>
            </Space>

            <div
              style={{
                fontSize: 12,
                color: "rgba(0, 0, 0, 0.45)",
                fontWeight: 50,
              }}
            >
              Esse painel contém {empresasFiltradas.length} empresas liberadas
            </div>
          </div>
        }
        extra={
          !protectedView && (
            <Button
              type="primary"
              onClick={() => {
                form.resetFields();
                setOpenCreate(true);
              }}
            >
              Nova empresa
            </Button>
          )
        }
        style={{
          backgroundColor: "#76F527",
          display: "flex",
          flexDirection: "column",
          minWidth: 400,
          minHeight: 300,
          maxHeight: "85vh",
        }}
        bodyStyle={{
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          flex: 1,
          overflow: "hidden",
        }}
      >
        <Space.Compact style={{ width: "96%" }}>
          <Input
            allowClear
            className="input-form"
            placeholder="Buscar por nome ou CNPJ"
            prefix={<SearchIcon size={14} />}
            onChange={(e) => setSearchTerm(e.target.value?.toLowerCase() || "")}
            style={{ height: "40px" }}
            autoComplete={false}
          />
        </Space.Compact>
        <Space.Compact style={{ width: "100%", marginBottom: 20 }}>
          <Select
            allowClear
            style={{ minWidth: 160 }}
            className="h40-select"
            placeholder="Estado"
            options={ESTADOS.map((e) => ({ value: e, label: e }))}
            onChange={setEstadoFiltro}
            value={estadoFiltro}
          />
          <Select
            allowClear
            style={{ minWidth: 200 }}
            className="h40-select"
            placeholder="Faixa de faturamento"
            options={FAIXAS.map((f) => ({ value: f, label: f }))}
            onChange={setFaixaFiltro}
            value={faixaFiltro}
          />
        </Space.Compact>

        <div
          className="custom-scroll"
          style={{ flex: 1, overflow: "auto", paddingRight: 20 }}
        >
          <List
            dataSource={empresasFiltradas}
            locale={{ emptyText: <Empty description="Nenhuma empresa" /> }}
            renderItem={(r) => (
              <List.Item
                style={{
                  backgroundColor: "#ffff",
                  marginBottom: 15,
                  padding: "8px 12px",
                  border: "1px solid #f0f0f0",

                  borderRadius: 10,
                  boxShadow:
                    "0 1px 1px rgba(0,0,0,0.08), 0 3px 2px rgba(0,0,0,0.18)",
                }}
                actions={[
                  !protectedView && (
                    <Popconfirm
                      title="Remover esta empresa?"
                      onConfirm={() => removeOne(r.id)}
                    >
                      <Trash2 size={14} style={{ cursor: "pointer" }} />
                    </Popconfirm>
                  ),
                ]}
              >
                <button
                  draggable
                  onDragStart={(e) => onDragStart?.(e, r.id)}
                  onClick={() => {
                    setSel(r);
                    setOpenDetailItem(true);
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    background: "#fff",
                    marginBottom: 8,
                    cursor: "pointer",
                    border: "none",
                  }}
                >
                  <Title level={5} style={{ margin: 0 }}>
                    {r.name}
                  </Title>
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary">CNPJ: </Text>
                    <Text>{r.document}</Text>
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <Space wrap>
                      <Tag color="geekblue">{r.state}</Tag>
                      <Tag color="green">{r.billing}</Tag>
                    </Space>
                  </div>
                </button>
              </List.Item>
            )}
          />
        </div>
      </Card>

      {/* Drawer de Detalhe (usa seu DrawerApp) */}
      <DrawerApp
        open={openDetailItem}
        onClose={onCloseDetailItem}
        title={sel ? sel.name : "Detalhes"}
        body={
          sel ? (
            <CompanyDetailBody
              empresaSel={sel}
              setEmpresaSel={setSel}
              setEmpresas={setEmpresas}
              ESTADOS={ESTADOS}
              FAIXAS={FAIXAS}
              uploadProps={uploadProps}
              openPdf={openPdf}
              typeView={"CRUD"}
            />
          ) : null
        }
        size="large"
        isOnlyView={true}
        width={outerWidth < 750 ? "100%" : "50%"}
      />

      {/* Drawer de Criação */}
      <DrawerApp
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        title="Nova empresa"
        body={
          <CreateCompanyForm
            ESTADOS={ESTADOS}
            FAIXAS={FAIXAS}
            onCancel={() => setOpenCreate(false)}
            onSubmit={save}
            form={form}
          />
        }
        size="large"
        isOnlyView={true}
        width={outerWidth < 750 ? "100%" : "50%"}
      />

      <Modal
        open={pdfModal.open}
        title={pdfModal.name}
        onCancel={closePdf}
        width={900}
        footer={null}
        bodyStyle={{ padding: 0, height: "75vh" }}
        destroyOnClose
      >
        {pdfModal.url ? (
          <iframe
            src={pdfModal.url}
            title="PDF"
            style={{ width: "100%", height: "100%", border: 0 }}
          />
        ) : null}
      </Modal>
    </Spin>
  );
}

/* ---------- Formulário de Criação ---------- */
function CreateCompanyForm({ ESTADOS, FAIXAS, onSubmit, onCancel, form }) {
  return (
    <Form form={form} layout="vertical" onFinish={onSubmit}>
      <Form.Item
        label="Empresa"
        name="name"
        rules={[{ required: true, message: "Informe o nome da empresa" }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="CNPJ"
        name="document"
        rules={[{ required: true, message: "Informe o CNPJ" }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Estado"
        name="state"
        rules={[{ required: true, message: "Selecione o estado" }]}
      >
        <Select options={ESTADOS.map((e) => ({ value: e, label: e }))} />
      </Form.Item>
      <Form.Item
        label="Faturamento"
        name="billing"
        rules={[{ required: true, message: "Selecione a faixa" }]}
      >
        <Select options={FAIXAS.map((f) => ({ value: f, label: f }))} />
      </Form.Item>

      <Row justify={"end"}>
        <Space style={{ marginTop: 8 }}>
          <Button onClick={onCancel}>Cancelar</Button>
          <Button
            type="primary"
            onClick={() => {
              form.submit();
            }}
          >
            Salvar
          </Button>
        </Space>
      </Row>
    </Form>
  );
}
